import React, { useState, useRef, useEffect } from 'react';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import { useAuthContext } from '../AuthProvider';
import UserTreeFiles from '../UserTreeFiles';
import LoginPage from '../LoginPage';
import SubscriptionsModal from './SubscriptionsModal';
import ConfirmationDialog from '../ConfirmationDialog';
import AlertModal from '../AlertModal';
import { trackEvent } from '../../utils/analytics';
import { DollarSign, Mail, FilePlus2, Crown, Sparkles, CreditCard, BarChart3 } from 'lucide-react';
import './UserMenu.css';
import { exportTreeToJSON } from '../../utils/treeExporter';
import { getCurrentSubscription, CurrentSubscription, getTierColor, formatSubscriptionStatus } from '../../utils/subscriptionApi';
import { PricingTier } from '../../types';
import { useFeatureAccess } from '../../hooks/useQuota';
import { incrementUsage } from '../../utils/firestoreApi';
import QuotaDisplay from '../QuotaDisplay';
import QuotasPage from '../QuotasPage';

const UserMenu: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChatPaneOpen, setIsChatPaneOpen] = useState(false);
  const [isUserTreeFilesOpen, setIsUserTreeFilesOpen] = useState(false);
  const [isLoginPageOpen, setIsLoginPageOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [isSubscriptionsModalOpen, setIsSubscriptionsModalOpen] = useState(false);
  const [subscription, setSubscription] = useState<CurrentSubscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showNewCanvasConfirm, setShowNewCanvasConfirm] = useState(false);
  const [showQuotasPage, setShowQuotasPage] = useState(false);
  
  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get Firebase auth context
  const { user, loading: authLoading, signOut } = useAuthContext();

  // Get whiteboard store methods and state
  const { newCanvas, state, showToast } = useWhiteboardStore();
  const { canCreateCanvas } = useFeatureAccess();
  const getSaveStatus = () => {
    const currentStateHash = exportTreeToJSON(state, { forComparison: true });
    if (!state.hasEverBeenSaved) return 'unsaved';
    if (state.lastSavedStateHash && currentStateHash === state.lastSavedStateHash) return 'saved';
    return 'tainted';
  };

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  // Fetch subscription data when user is authenticated
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setSubscription(null);
        return;
      }

      setSubscriptionLoading(true);
      try {
        const currentSub = await getCurrentSubscription();
        setSubscription(currentSub);
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  // Detect if chat pane is open by checking if any note is selected
  useEffect(() => {
    const hasSelectedNote = state.selectedNoteIds.length > 0;
    setIsChatPaneOpen(hasSelectedNote);
  }, [state.selectedNoteIds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAvatarClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNewCanvas = async () => {
    // Check canvas creation quota if user is authenticated
    if (user) {
      try {
        const canCreate = await canCreateCanvas();
        if (!canCreate) {
          showAlert(
            'Canvas Creation Limit Reached',
            'You\'ve reached your canvas limit. Please upgrade your plan to create more canvases.',
            'danger'
          );
          setIsDropdownOpen(false);
          return;
        }
      } catch (error) {
        console.error('Error checking canvas creation quota:', error);
        // Fallback: allow canvas creation if quota check fails
      }
    }

    const status = getSaveStatus();
    if (status === 'tainted' || (status === 'unsaved' && state.notes.length > 0)) {
      setShowNewCanvasConfirm(true);
    } else {
      // Create new canvas without incrementing quota
      // Canvas counter will be incremented when the canvas is actually saved
      newCanvas();
    }
    setIsDropdownOpen(false);
  };

  const handleConfirmNewCanvas = async () => {
    // Create new canvas without incrementing quota
    // Canvas counter will be incremented when the canvas is actually saved
    newCanvas();
    setShowNewCanvasConfirm(false);
  };

  const handleCancelNewCanvas = () => {
    setShowNewCanvasConfirm(false);
  };

  const handleLogin = () => {
    setIsLoginPageOpen(true);
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    setShowSignOutConfirm(true);
    setIsDropdownOpen(false);
  };

  const confirmSignOut = async () => {
    try {
      await signOut();
      trackEvent('logout', { userId: user?.uid });
      setShowSignOutConfirm(false);
      setIsLoginPageOpen(true);
    } catch (error) {
      console.error('Sign out error:', error);
      // Keep modal open if sign out fails
    }
  };

  const cancelSignOut = () => {
    setShowSignOutConfirm(false);
  };

  const handleMyThreadIts = () => {
    setIsUserTreeFilesOpen(true);
    setIsDropdownOpen(false);
  };

  const handleMySubscriptions = () => {
    setIsSubscriptionsModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleViewQuotas = () => {
    setShowQuotasPage(true);
    setIsDropdownOpen(false);
  };

  const handleSubscriptionClick = () => {
    if (subscription && subscription.tier === PricingTier.Pro) {
      window.location.href = '/subscription';
    } else {
      window.location.href = '/pricing';
    }
    setIsDropdownOpen(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderSubscriptionBadge = () => {
    if (subscriptionLoading) {
      return (
        <div className="subscription-badge subscription-badge-loading">
          <div className="loading-spinner-small"></div>
          <span>Loading...</span>
        </div>
      );
    }

    if (!subscription || subscription.tier === PricingTier.Free) {
      return (
        <div className="subscription-badge subscription-badge-free clickable" onClick={handleSubscriptionClick}>
          <Sparkles className="subscription-badge-icon" />
          <span>Free Plan</span>
        </div>
      );
    }

    // For Basic plans, don't make it clickable
    if (subscription.tier === PricingTier.Basic) {
      const isActive = subscription.subscription && ['active', 'trialing'].includes(subscription.subscription.status);
      return (
        <div className={`subscription-badge subscription-badge-${subscription.tier.toLowerCase()} ${isActive ? 'active' : 'inactive'}`}>
          <Crown className="subscription-badge-icon" />
          <span>{subscription.tier} Plan</span>
          {subscription.subscription && (
            <span className="subscription-status">
              {subscription.subscription.billingCycle}
            </span>
          )}
        </div>
      );
    }

    // For Pro plans, make it clickable
    const isActive = subscription.subscription && ['active', 'trialing'].includes(subscription.subscription.status);

    return (
      <div className={`subscription-badge subscription-badge-${subscription.tier.toLowerCase()} ${isActive ? 'active' : 'inactive'} clickable`} onClick={handleSubscriptionClick}>
        <Crown className="subscription-badge-icon" />
        <span>{subscription.tier} Plan</span>
        {subscription.subscription && (
          <span className="subscription-status">
            {subscription.subscription.billingCycle}
          </span>
        )}
      </div>
    );
  };

  const renderAvatar = () => {
    if (authLoading) {
      return (
        <div className="user-avatar-loading">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    if (user?.photoURL) {
      return (
        <img 
          src={user.photoURL} 
          alt={user.displayName || 'User'}
          className="user-avatar-image"
        />
      );
    }

    return (
      <div className="user-avatar-initials">
        {user ? getInitials(user.displayName || 'User') : '?'}
      </div>
    );
  };

  return (
    <div className="user-menu-container">
      {/* Avatar Button */}
      <button
        ref={buttonRef}
        onClick={handleAvatarClick}
        className={`user-avatar-button ${isChatPaneOpen ? 'chat-active' : ''}`}
        title={user ? (user.displayName || 'User') : 'Sign in'}
        aria-label={user ? `User menu for ${user.displayName || 'User'}` : 'Sign in'}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        {renderAvatar()}
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div ref={dropdownRef} className="user-dropdown">
          {user ? (
            <>
              {/* User Info Section */}
              <div className="user-info">
                <div className="user-name">{user.displayName}</div>
                {renderSubscriptionBadge()}
                <div className="user-email">{user.email}</div>
              </div>
              
              {/* Quota Display */}
              <QuotaDisplay variant="compact" showUpgradePrompt={false} />
              
              <div className="dropdown-divider"></div>

              {/* Menu Items */}
              <button onClick={handleNewCanvas} className="dropdown-item">
                <FilePlus2 className="dropdown-icon" />
                New Canvas
              </button>
              
              {/* Menu Items */}
              <button 
                onClick={handleMyThreadIts}
                className="dropdown-item"
              >
                <svg className="dropdown-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                My Canvases
              </button>

              <button 
                onClick={handleMySubscriptions}
                className="dropdown-item"
              >
                <CreditCard className="dropdown-icon" />
                My Subscriptions
              </button>

              <button 
                onClick={handleViewQuotas}
                className="dropdown-item"
              >
                <BarChart3 className="dropdown-icon" />
                View Quotas
              </button>

              {subscription && subscription.tier === PricingTier.Pro && (
                <a href="/subscription" className="dropdown-item">
                  <Crown className="dropdown-icon" />
                  Manage Subscription
                </a>
              )}
              
              <div className="dropdown-divider"></div>

              <a href="/pricing" className="dropdown-item">
                <DollarSign className="dropdown-icon" />
                Pricing
              </a>

              <a href="/contact" className="dropdown-item">
                <Mail className="dropdown-icon" />
                Contact Us
              </a>

              <div className="dropdown-divider"></div>

              <button 
                onClick={handleLogout}
                className="dropdown-item dropdown-item-danger"
              >
                <svg className="dropdown-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </>
          ) : (
            <button 
              onClick={handleLogin}
              className="dropdown-item dropdown-item-primary"
            >
              <svg className="dropdown-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Sign In with Google
            </button>
          )}
        </div>
      )}

      {/* UserTreeFiles Modal */}
      <UserTreeFiles 
        isOpen={isUserTreeFilesOpen} 
        onClose={() => setIsUserTreeFilesOpen(false)} 
      />

      {/* Subscriptions Modal */}
      <SubscriptionsModal 
        isOpen={isSubscriptionsModalOpen} 
        onClose={() => setIsSubscriptionsModalOpen(false)} 
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        title={alertModalConfig.title}
        message={alertModalConfig.message}
        buttonText={alertModalConfig.buttonText}
        buttonVariant={alertModalConfig.buttonVariant}
      />

      {/* Login Page */}
      <LoginPage 
        isOpen={isLoginPageOpen}
        onLoginSuccess={(user) => {
          // User is automatically set by Firebase auth state
          setIsLoginPageOpen(false);
        }}
        onClose={() => setIsLoginPageOpen(false)}
      />

      {/* Sign Out Confirmation Modal */}
      {showSignOutConfirm && (
        <div className="sign-out-overlay" onClick={cancelSignOut}>
          <div className="sign-out-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sign-out-header">
              <h3>Sign Out</h3>
              <p>Are you sure you want to sign out?</p>
            </div>
            <div className="sign-out-actions">
              <button className="cancel-button" onClick={cancelSignOut}>
                Cancel
              </button>
              <button className="confirm-button" onClick={confirmSignOut}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Canvas Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showNewCanvasConfirm}
        title="Start New Canvas"
        message="You have unsaved changes. Are you sure you want to start a new canvas? Your current changes will be lost."
        confirmText="Start New Canvas"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmNewCanvas}
        onCancel={handleCancelNewCanvas}
      />

      {/* Quotas Page */}
      {showQuotasPage && (
        <QuotasPage onClose={() => setShowQuotasPage(false)} />
      )}
    </div>
  );
};

export default UserMenu;
